import { Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography } from '@material-ui/core';
import { IconButton } from '@material-ui/core';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { OptionType } from '../EmployeesComboboxMI';
import AuthUserAuthorizationsService, { IUserAuthorizationDataRow } from '../RelationAuthorizations/AuthUserAuthorizationsService';
import { ITableRefApi, IUserAuthorizationsWindowConfig, UserAuthorizationsTable } from '../RelationAuthorizations/UserAuthorizationsWindow';
import SearchIcon from '@material-ui/icons/Search';
import useDebounce from '../../utils/useDebounce';
import DataSersIcon from '../../components/DataSetsIcon';
import { useGetEmployeDetailsApi } from '../../services/useEmployeeDetailsApi';

export interface IUserAuthorizationsPreviewWindowConfig {
	show: boolean;
	filterId: string;
	filter: string;
	logFilters: string;
	columns: { [key: string]: IColumn }
}

interface IColumn {
	label: string;
	minWidth: number;
	searched: boolean;
	className: string;
}

interface IProps {
	issueId: number | string;
	user: OptionType;
	config: IUserAuthorizationsPreviewWindowConfig;
}

export default function UserAuthorizationsPreviewWindow(props: IProps) {
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [rowsPerUser, setRowsPerUser] = React.useState<IUserAuthorizationDataRow[]>([]);
	const [dataSetsResult, setDataSetsResult] = React.useState<'Success' | 'NotPermission' | 'EmployeeNotFound'>();
	const [search, setSearch] = React.useState("");
	const [searchLazy, setSearchLazy] = React.useState("");
	const debouncedSearchTerm = useDebounce(search, 1500);
	const selected1Ref = React.useRef<ITableRefApi>();
	const [employeDetails, setLogId, isLoading, isError] = useGetEmployeDetailsApi();

	React.useEffect(() => {
		setSearchLazy(search);
	}, [debouncedSearchTerm]);

	React.useEffect(() => {
		if (props.user && open) {
			setLoading(true);
			new AuthUserAuthorizationsService().getRelatedDataSetsResources(
				props.issueId,
				props.user.value,
				props.config?.filterId,
				props.config?.filter,
				props.config?.logFilters,
				(data, status) => {
					setRowsPerUser(data ?? []);
					setDataSetsResult(status);
					setLoading(false);
				},
			);

			setLogId(props.user.value);
		}
	}, [props.user, open])

	function handleClickOpen() {
		setOpen(true);
	}

	function handleClose() {
		setOpen(false);
	}

	if (!props.user?.value)
		return null;

	if (!props.config?.show)
		return null;

	const defaultConfig: IUserAuthorizationsWindowConfig = {
		filters: [],
		columns: [
			{ id: 'name', disablePadding: false, labelPl: t("resources.authorizations.dataSet"), minWidth: 230, searched: true },
			{ id: 'authorizationLevel', disablePadding: false, labelPl: t("resources.authorizations.authorizationLevel"), searched: true },
			{ id: 'relationNumber', disablePadding: false, labelPl: t("resources.authorizations.authorizationNumber"), searched: true },
			{ id: 'issueId', disablePadding: false, labelPl: t("resources.authorizations.issueId"), searched: false },
			{ id: 'period', disablePadding: false, labelPl: t("resources.authorizations.period"), minWidth: 170, searched: false },
			{ id: 'relationStatus', disablePadding: false, labelPl: t("resources.authorizations.relationStatus"), searched: false },
		]
	};

	if (props.config.columns) {
		defaultConfig.columns = [];
		for (let propColumn in props.config.columns) {

			const item = props.config.columns[propColumn];
			defaultConfig.columns.push({
				id: propColumn as keyof IUserAuthorizationDataRow,
				disablePadding: false,
				labelPl: item.label,
				minWidth: item.minWidth,
				searched: item.searched,
				className: item.className,
			})
		}
	};

	return (
		<div className="user-accesses-window">
			<IconButton type="button" className="qd-btn qd-btn-default" onClick={handleClickOpen} title={t("resources.authorizations.authorizationsFor")}>
				<DataSersIcon />
			</IconButton>
			<Dialog
				fullWidth
				maxWidth="lg"
				open={open}
				onClose={handleClose}
				className="user-authorization-window-dialog"
			>
				<DialogTitle>
					<TextField
						variant="outlined"
						placeholder={t("common.action.search") + "..."}

						value={search}
						onChange={(e) => setSearch(e.target.value)}
						size="small"
						style={{
							float: "right"
						}}
						InputProps={{
							startAdornment: <InputAdornment position="start" > <SearchIcon color="disabled" /></InputAdornment>
						}}
					/>

					{t("resources.authorizations.authorizationsFor")}: {props.user.label}
					{employeDetails?.position && <span title={t("resources.authorizations.position")}>{' '}({employeDetails.position})</span>}
				</DialogTitle>
				<DialogContent>
					{dataSetsResult === 'Success' && <UserAuthorizationsTable
						blockedRelationIds={() => []}
						canSelect={false}
						rowsPerUser={rowsPerUser}
						ref={selected1Ref}
						config={defaultConfig}
						search={searchLazy}
					/>}
					{dataSetsResult && dataSetsResult !== 'Success' && <Typography color="error">
						{t("resources.authorizations.resultStatus." + dataSetsResult)}
					</Typography>}
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={handleClose} autoFocus>
						{t("common.action.close")}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}